import dateTimeService from "app/services/date-time/dateTimeService";

import { DEAL_SIGNAL_MAX_PROCESSING_MS } from "../constants";
import type { SignalRecommenderFileStatus } from "../types/model";
import { signalRecommenderFileStatusMap } from "../types/model";

type DealSignalStatusOnly = { readonly status: SignalRecommenderFileStatus };

type DealSignalRun = DealSignalStatusOnly & { readonly created: string };

/** Only the optimistic placeholder builds a `created` with one; the list endpoint sends a bare date. */
const TIME_OF_DAY_PATTERN = /\d{2}:\d{2}/;

const END_OF_DAY_MS = 24 * 60 * 60 * 1000 - 1;

export const isDealSignalProcessing = (file: DealSignalStatusOnly): boolean =>
    file.status === signalRecommenderFileStatusMap.inProgress;

export const isDealSignalSucceeded = (file: DealSignalStatusOnly): boolean =>
    file.status === signalRecommenderFileStatusMap.succeeded;

export const isDealSignalFailed = (file: DealSignalStatusOnly): boolean =>
    file.status === signalRecommenderFileStatusMap.failed;

/**
 * The latest instant a `created` value can stand for.
 *
 * A bare date is an interval, not a moment. Measuring it from UTC midnight — its earliest instant —
 * makes every run arrive already hours old, which is why it used to be left alone entirely; but that
 * left nothing to measure at all, so no run the backend stored could ever stall and the poll below ran
 * for as long as the tab stayed open. Measuring from the end of the day instead cannot report a run as
 * older than it is: a run recorded today is always still in the future, and one recorded earlier is
 * past the window whatever time of day it actually started.
 */
const resolveLatestCreatedAt = (created: string): number | null => {
    const parsed = dateTimeService.getUTCMoment(created);

    if (!parsed.isValid()) {
        return null;
    }

    return TIME_OF_DAY_PATTERN.test(created) ? parsed.valueOf() : parsed.valueOf() + END_OF_DAY_MS;
};

/**
 * A run the backend still calls in-progress that has outlived the processing window.
 *
 * Measured from `created` rather than from a poll counter so the verdict survives a reload, a remount
 * and a second tab — a counter resets with the component and would restart the wait every time.
 */
export const isDealSignalStalled = (file: DealSignalRun, now: number = Date.now()): boolean => {
    if (!isDealSignalProcessing(file)) {
        return false;
    }

    const createdAt = resolveLatestCreatedAt(file.created);

    return createdAt !== null && now - createdAt > DEAL_SIGNAL_MAX_PROCESSING_MS;
};

/** In progress and still within the window — the only state worth spending another poll on. */
export const isDealSignalActivelyProcessing = (file: DealSignalRun, now: number = Date.now()): boolean =>
    isDealSignalProcessing(file) && !isDealSignalStalled(file, now);
